import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { api } from "../../api";
import { loadRuntimeConfig, type AuthConfig } from "../../runtimeConfig";
import { AuthContext, useAdminAuth, type AdminAuth } from "./AuthContext";
import AuthScreen, { AuthLoading } from "./AuthScreen";
import LocalLogin from "./LocalLogin";

// Clerk's SDK is only downloaded where Clerk signs admins in, and never by the public player.
const ClerkAuthRoot = lazy(() => import("./ClerkAuth"));

/** Wraps every /admin route: finds out how this environment signs admins in, then provides it to them. */
export default function AdminAuthRoot() {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [failed, setFailed] = useState(false);
  const load = () => { setFailed(false); loadRuntimeConfig().then((c) => setConfig(c.auth)).catch(() => setFailed(true)); };
  useEffect(load, []);

  if (failed) {
    return <AuthScreen title="Can't reach DemoDive" message="The server didn't answer. Check your connection and try again."
      actions={<button type="button" className="btn btn-primary" onClick={load}>Try again</button>} />;
  }
  if (!config) return <AuthLoading />;
  if (config.mode === "clerk") return <Suspense fallback={<AuthLoading />}><ClerkAuthRoot publishableKey={config.publishableKey} /></Suspense>;
  const auth: AdminAuth = config.mode === "local"
    ? { mode: "local", signedIn: null, signOut: async () => { await api.logout(); } }
    : { mode: "unconfigured", signedIn: false, signOut: async () => {}, message: config.message };
  return <AuthContext.Provider value={auth}><Outlet /></AuthContext.Provider>;
}

/** /admin/login/*: Clerk's sign-in (with password reset and enterprise SSO), or the local development password. */
export function SignInRoute() {
  const auth = useAdminAuth();
  if (auth.SignIn) return <auth.SignIn />;
  if (auth.mode === "local") return <LocalLogin />;
  return <AuthScreen title="Sign-in isn't set up yet" message={auth.message} />;
}

/** /admin/sign-up/*: Clerk's sign-up. Without Clerk there's nothing to sign up for. */
export function SignUpRoute() {
  const auth = useAdminAuth();
  if (auth.SignUp) return <auth.SignUp />;
  return <Navigate to="/admin/login" replace />;
}
